import React from 'react';
import { Box, Flex, Text, Link } from 'rebass/styled-components';
import { Input } from '@rebass/forms/styled-components';
import { Button, FormSelect, FormAsyncSelect, Logo } from '@getro/rombo';
import { usePublicAgentForm, AGENT_OPTIONS } from './hooks/usePublicAgentForm';

export const SharedAgentConfig = () => {
  const {
    queryParams,
    additionalParamsError,
    updatedLink,
    isAllInputsFilled,
    handleParamChange,
    handleOpenLink,
    handleGenerateToken,
    selectedCollection,
    handleCollectionChange,
    collectionOptions,
    selectedAgent,
    handleAgentChange,
    selectedJob,
    handleJobChange,
    handleJobSearch,
    handleJobMenuOpen,
    jobDefaultOptions,
    isLoadingJobs,
  } = usePublicAgentForm();
  return (
    <Box>
      <Box
        px="16px"
        py="16px"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'border.subtle',
        }}
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          sx={{
            gap: '16px',
          }}
        >
          <Flex sx={{ gap: '16px', alignItems: 'center', flex: 1 }}>
            <Text fontSize="18px" fontWeight="600" color="text.dark" lineHeight="1.4">
              AI Recruiter - Agent shared view configuration
            </Text>
          </Flex>

          {/* Right side: Getro logo */}
          <Flex alignItems="center" sx={{ flexShrink: 0 }}>
            <Link href="https://www.getro.com" target="_blank" rel="noopener noreferrer">
              <Logo color="primary" width="96px" />
            </Link>
          </Flex>
        </Flex>
      </Box>
      <Flex flexDirection="column" sx={{ gap: '16px', px: '16px', py: '16px' }}>
        <Text fontSize="14px" color="text.main" mb="8px">
          Configure the query parameters for the agent session:
        </Text>

        <Flex flexDirection="column" sx={{ gap: '12px' }}>
          <Box>
            <Text fontSize="12px" fontWeight="500" color="text.main" mb="4px">
              Account{' '}
              <Text as="span" color="red">
                *
              </Text>
            </Text>
            <FormSelect
              value={selectedCollection}
              onChange={handleCollectionChange}
              options={collectionOptions}
              placeholder="Select an account (required)"
              isClearable
              isSearchable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          </Box>
          <Box>
            <Text fontSize="12px" fontWeight="500" color="text.main" mb="4px">
              Token
            </Text>
            <Flex sx={{ gap: '8px' }}>
              <Input
                value={queryParams.jwt}
                onChange={(e) => handleParamChange('jwt', e.target.value)}
                placeholder="Enter JWT (optional)"
                disabled
                sx={{
                  fontSize: '14px',
                  border: '1px solid',
                  borderColor: 'border.subtle',
                  borderRadius: '4px',
                  px: '12px',
                  py: '8px',
                  flex: 1,
                }}
              />
              <Button
                onClick={handleGenerateToken}
                sx={{
                  px: '16px',
                  py: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: 'primary',
                  color: 'white',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
              >
                Generate
              </Button>
            </Flex>
          </Box>

          <Box>
            <Text fontSize="12px" fontWeight="500" color="text.main" mb="4px">
              Agent{' '}
              <Text as="span" color="red">
                *
              </Text>
            </Text>
            <FormSelect
              value={selectedAgent}
              onChange={handleAgentChange}
              options={AGENT_OPTIONS}
              placeholder="Select or type an agent ID (required)"
              isClearable
              isSearchable
              isCreatable
              isValidNewOption={(inputValue) => Boolean(inputValue?.trim())}
              formatCreateLabel={(inputValue) => `Use custom agent id: ${inputValue}`}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          </Box>

          <Box>
            <Text fontSize="12px" fontWeight="500" color="text.main" mb="4px">
              Job{' '}
              <Text as="span" color="red">
                *
              </Text>
            </Text>
            <FormAsyncSelect
              value={selectedJob}
              onChange={handleJobChange}
              loadOptions={handleJobSearch}
              defaultOptions={jobDefaultOptions}
              onMenuOpen={handleJobMenuOpen}
              isLoading={isLoadingJobs}
              placeholder="Search for a job (required)"
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          </Box>

          <Box>
            <Text fontSize="12px" fontWeight="500" color="text.main" mb="4px">
              Agent title
            </Text>
            <Input
              value={queryParams.empty_state_title}
              onChange={(e) => handleParamChange('empty_state_title', e.target.value)}
              placeholder="Enter agent title (optional)"
              sx={{
                fontSize: '14px',
                border: '1px solid',
                borderColor: 'border.subtle',
                borderRadius: '4px',
                px: '12px',
                py: '8px',
              }}
            />
          </Box>

          <Box>
            <Text fontSize="12px" fontWeight="500" color="text.main" mb="4px">
              Agent subtitle
            </Text>
            <Input
              value={queryParams.empty_state_subtitle}
              onChange={(e) => handleParamChange('empty_state_subtitle', e.target.value)}
              placeholder="Enter agent subtitle (optional)"
              sx={{
                fontSize: '14px',
                border: '1px solid',
                borderColor: 'border.subtle',
                borderRadius: '4px',
                px: '12px',
                py: '8px',
              }}
            />
          </Box>

          <Box>
            <Text fontSize="12px" fontWeight="500" color="text.main" mb="4px">
              Agent initial commands
            </Text>
            <Input
              value={queryParams.initial_prompts}
              onChange={(e) => handleParamChange('initial_prompts', e.target.value)}
              placeholder="Enter prompts (comma-separated or JSON array) (optional)"
              sx={{
                fontSize: '14px',
                border: '1px solid',
                borderColor: 'border.subtle',
                borderRadius: '4px',
                px: '12px',
                py: '8px',
              }}
            />
          </Box>

          <Box>
            <Text fontSize="12px" fontWeight="500" color="text.main" mb="4px">
              Additional parameters
            </Text>
            <Input
              value={queryParams.additional_params}
              onChange={(e) => handleParamChange('additional_params', e.target.value)}
              placeholder="e.g., icp_id=123456&vc_url=unicornland.vc (optional)"
              sx={{
                fontSize: '14px',
                border: '1px solid',
                borderColor: additionalParamsError ? 'red' : 'border.subtle',
                borderRadius: '4px',
                px: '12px',
                py: '8px',
              }}
            />
            {additionalParamsError ? (
              <Text fontSize="11px" color="red" mt="4px">
                {additionalParamsError}
              </Text>
            ) : (
              <Text fontSize="11px" color="text.subtle" mt="4px">
                Special characters (spaces, etc.) will be automatically URL-encoded
              </Text>
            )}
          </Box>
        </Flex>

        <Box>
          <Text fontSize="12px" fontWeight="500" color="text.main" mb="4px">
            Updated Link
          </Text>
          <Flex sx={{ gap: '8px' }}>
            <Input
              value={updatedLink}
              readOnly
              sx={{
                fontSize: '14px',
                border: '1px solid',
                borderColor: 'border.subtle',
                borderRadius: '4px',
                px: '12px',
                py: '8px',
                flex: 1,
                backgroundColor: 'background.main',
              }}
            />
            <Button
              onClick={handleOpenLink}
              disabled={!isAllInputsFilled}
              sx={{
                px: '16px',
                py: '8px',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '4px',
                cursor: isAllInputsFilled ? 'pointer' : 'not-allowed',
                border: 'none',
                backgroundColor: isAllInputsFilled ? 'primary' : 'gray.400',
                color: 'white',
                opacity: isAllInputsFilled ? 1 : 0.6,
                '&:hover': {
                  backgroundColor: isAllInputsFilled ? 'primary.dark' : 'gray.400',
                },
              }}
            >
              Open
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};
